import { useEffect, useRef, useState } from "react";
import { GanttChartSquare, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useGantt } from "@/features/projetos/hooks/useProjetos";
import type { GanttTarefa } from "@/features/projetos/types";

const DIA = 86_400_000;
const ROWH = 34; // altura de cada linha (px)
const BARH = 26; // altura da barra (px)
const parse = (s: string) => new Date(s.slice(0, 10) + "T00:00:00");
const fmtData = (s: string | null) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "—");

export default function ProjetoGanttTab({ projetoId }: { projetoId: number }) {
  const { data: gantt, isLoading } = useGantt(projetoId);
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackW, setTrackW] = useState(0);

  useEffect(() => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    const ro = new ResizeObserver(() => setTrackW(el.clientWidth));
    ro.observe(el);
    setTrackW(el.clientWidth);
    return () => ro.disconnect();
  }, [gantt]);

  if (isLoading) return <Card><CardContent className="pt-6 text-muted-foreground">Carregando...</CardContent></Card>;
  if (!gantt) return null;

  const comDatas = gantt.tarefas.filter(t => t.dtInicio && t.dtFim);
  const semDatas = gantt.tarefas.filter(t => !t.dtInicio || !t.dtFim);

  const inicio = gantt.inicio ? parse(gantt.inicio) : null;
  const fim = gantt.fim ? parse(gantt.fim) : null;
  const totalDias = inicio && fim ? Math.max(1, (fim.getTime() - inicio.getTime()) / DIA + 1) : 1;

  // Geometria (px) de cada tarefa com datas — para barras e setas.
  const idx = new Map<number, number>();
  comDatas.forEach((t, i) => idx.set(t.id, i));
  const geo = (t: GanttTarefa) => {
    const ini = parse(t.dtInicio!).getTime();
    const f = parse(t.dtFim!).getTime();
    const leftPct = inicio ? ((ini - inicio.getTime()) / DIA / totalDias) * 100 : 0;
    const widthPct = ((f - ini) / DIA + 1) / totalDias * 100;
    const leftPx = (Math.max(0, leftPct) / 100) * trackW;
    const widthPx = (Math.max(0.5, widthPct) / 100) * trackW;
    const row = idx.get(t.id) ?? 0;
    return { leftPx, widthPx, leftPct: Math.max(0, leftPct), widthPct: Math.max(2, widthPct), yCenter: row * ROWH + ROWH / 2 };
  };
  const byId = new Map(comDatas.map(t => [t.id, t]));

  // Conectores de dependência (predecessora → sucessora) conforme o tipo.
  const conectores = gantt.dependencias
    .filter(d => byId.has(d.tarefaId) && byId.has(d.tarefaPredecessoraId))
    .map(d => {
      const pred = geo(byId.get(d.tarefaPredecessoraId)!);
      const succ = geo(byId.get(d.tarefaId)!);
      // tipo: 0 FS, 1 SS, 2 FF, 3 SF
      const x1 = d.tipo === 1 || d.tipo === 3 ? pred.leftPx : pred.leftPx + pred.widthPx; // saída
      const x2 = d.tipo === 2 || d.tipo === 3 ? succ.leftPx + succ.widthPx : succ.leftPx; // entrada
      const critico = (byId.get(d.tarefaPredecessoraId)?.critico && byId.get(d.tarefaId)?.critico) ?? false;
      return { id: d.id, x1, y1: pred.yCenter, x2, y2: succ.yCenter, critico };
    });

  const altura = comDatas.length * ROWH;
  const nomeCritico = gantt.caminhoCritico.map(id => gantt.tarefas.find(t => t.id === id)?.nome ?? `#${id}`);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><GanttChartSquare className="h-4 w-4" /> Cronograma (Gantt)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge variant="outline">Início: {fmtData(gantt.inicio)}</Badge>
            <Badge variant="outline">Fim: {fmtData(gantt.fim)}</Badge>
            <Badge variant="outline">Duração: {Number(gantt.duracaoProjetoDias)} dia(s)</Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><span className="inline-block h-3 w-3 rounded bg-blue-500" /> Normal</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><span className="inline-block h-3 w-3 rounded bg-red-500" /> Caminho crítico</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><svg width="22" height="10"><line x1="0" y1="5" x2="16" y2="5" stroke="#94a3b8" strokeWidth="1.5" /><path d="M16 2 L21 5 L16 8 Z" fill="#94a3b8" /></svg> Dependência</span>
          </div>

          {comDatas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Defina datas planejadas (início e fim) nas tarefas para visualizar o cronograma.</p>
          ) : (
            <div className="flex">
              {/* coluna de rótulos */}
              <div className="w-48 shrink-0">
                {comDatas.map(t => (
                  <div key={t.id} className="flex items-center truncate text-sm" style={{ height: ROWH }} title={t.nome}>
                    {t.critico && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 shrink-0 text-red-500" />}
                    <span className="truncate">{t.nome}</span>
                  </div>
                ))}
              </div>
              {/* área das barras + setas */}
              <div ref={trackRef} className="relative flex-1" style={{ height: altura }}>
                {comDatas.map(t => {
                  const g = geo(t);
                  const cor = t.critico ? "bg-red-500" : "bg-blue-500";
                  const corProg = t.critico ? "bg-red-700" : "bg-blue-700";
                  return (
                    <div key={t.id} className={`absolute rounded ${cor}`}
                      style={{ top: (idx.get(t.id) ?? 0) * ROWH + (ROWH - BARH) / 2, height: BARH, left: `${g.leftPct}%`, width: `${g.widthPct}%` }}
                      title={`${fmtData(t.dtInicio)} – ${fmtData(t.dtFim)} · ${Number(t.duracaoDias)}d · folga ${Number(t.folga)}d`}>
                      <div className={`h-full rounded-l ${corProg}`} style={{ width: `${Math.min(100, Number(t.percentualConcluido))}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-white">{Number(t.percentualConcluido).toFixed(0)}%</span>
                    </div>
                  );
                })}
                {trackW > 0 && (
                  <svg className="pointer-events-none absolute inset-0" width={trackW} height={altura}>
                    <defs>
                      <marker id="seta-normal" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6 Z" fill="#94a3b8" /></marker>
                      <marker id="seta-critica" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6 Z" fill="#ef4444" /></marker>
                    </defs>
                    {conectores.map(c => {
                      const out = c.x1 + 10;
                      const d = `M ${c.x1} ${c.y1} H ${out} V ${c.y2} H ${c.x2}`;
                      return (
                        <path key={c.id} d={d} fill="none"
                          stroke={c.critico ? "#ef4444" : "#94a3b8"} strokeWidth={c.critico ? 1.8 : 1.3}
                          markerEnd={`url(#${c.critico ? "seta-critica" : "seta-normal"})`} />
                      );
                    })}
                  </svg>
                )}
              </div>
            </div>
          )}

          {semDatas.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {semDatas.length} tarefa(s) sem datas planejadas não aparecem no cronograma: {semDatas.map(t => t.nome).join(", ")}.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-red-500" /> Caminho crítico</CardTitle></CardHeader>
        <CardContent>
          {nomeCritico.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma tarefa no caminho crítico (defina dependências e durações).</p>
          ) : (
            <p className="text-sm">{nomeCritico.join("  →  ")}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
